import { useEffect, useId, useRef, useState } from "react";
import type { FocusEvent, JSX, KeyboardEvent } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router";
import { routePaths } from "../../app/navigation";
import { formatHoursMinutes } from "../../domain/duration";
import type { LocalDate } from "../../domain/time";
import { useWhileShown } from "../../shared/query/useWhileShown";
import { useI18n } from "../../shared/i18n/I18nContext";
import { useHasEmployment } from "../../shared/session/useHasEmployment";
import { useNavigationRoles } from "../../shared/session/useNavigationRoles";
import { Alert } from "../../shared/ui/Alert";
import { cx } from "../../shared/ui/classNames";
import { useFocusOnMount } from "../../shared/ui/focus";
import { Icon } from "../../shared/ui/Icon";
import { useUnsavedInput } from "../../shared/ui/unsavedInput";
import styles from "../../shared/ui/ui.module.css";
import clockStyles from "./ClockPage.module.css";
import compactStyles from "./CompactClock.module.css";
import { EarlyStartConfirmation } from "./EarlyStartConfirmation";
import { LocationNotice } from "./LocationNotice";
import { compactClockSeconds, showsCompactClock } from "./model/compactClock";
import { dayOverviewHandover, dayOverviewRequest } from "./model/dayOverviewHandover";
import { ClockNfc } from "./NfcClock";
import { ClockProjectSwitcher } from "./ProjectSwitcher";
import { useClockControl } from "./useClockControl";
import { WarningList } from "./WarningList";

/** Interval of the compact clock: it shows whole minutes, so it need not render every second. */
const compactTickMs = 10_000;

/** Controls that take the focus inside the panel, in document order. */
const focusableSelector =
  'a[href], button:not([disabled]), select:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Properties of {@link ClockOutConfirmation}. */
interface ClockOutConfirmationProps {
  /** Records „Gehen“. */
  readonly onConfirm: () => void;
  /** Closes the confirmation without recording. */
  readonly onCancel: () => void;
}

/**
 * The short confirmation of „Gehen“ in the compact clock (F-75): ending the working day is undone
 * only by a correction request, and the panel lies above other content, so one more tap guards
 * against a slip. It takes the focus, and Escape cancels it.
 *
 * @param props - Component properties.
 * @returns The confirmation.
 */
function ClockOutConfirmation(props: ClockOutConfirmationProps): JSX.Element {
  const { t } = useI18n();
  const groupRef = useFocusOnMount<HTMLDivElement>();
  const textId = useId();

  const confirmation = (
    <div
      ref={groupRef}
      role="group"
      aria-labelledby={textId}
      tabIndex={-1}
      className={cx(styles["alert"], styles["alertWarning"], styles["confirmAction"])}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.preventDefault();
          event.stopPropagation();
          props.onCancel();
        }
      }}
    >
      <p id={textId}>{t("compactClock.confirmClockOut")}</p>
      <div className={styles["buttonGrid"]}>
        <button type="button" className={styles["button"]} onClick={props.onCancel}>
          {t("common.cancel")}
        </button>
        <button
          type="button"
          className={cx(styles["button"], styles["primary"])}
          onClick={props.onConfirm}
        >
          {t("compactClock.confirmClockOutAction")}
        </button>
      </div>
    </div>
  );

  return confirmation;
}

/** Properties of {@link OverviewQuestion}. */
interface OverviewQuestionProps {
  /** Leads to the day overview on „Heute“. */
  readonly onGo: () => void;
  /** Keeps the page and closes the panel. */
  readonly onStay: () => void;
}

/**
 * The question before the compact clock leads to the day overview on „Heute“ while the current page
 * holds unsaved input (F-78): leaving the page would drop it. It takes the focus; Escape closes the
 * panel like „Hier bleiben“.
 *
 * @param props - Component properties.
 * @returns The question.
 */
function OverviewQuestion(props: OverviewQuestionProps): JSX.Element {
  const { t } = useI18n();
  const groupRef = useFocusOnMount<HTMLDivElement>();
  const textId = useId();

  const question = (
    <div
      ref={groupRef}
      role="group"
      aria-labelledby={textId}
      tabIndex={-1}
      className={cx(styles["alert"], styles["alertInfo"], styles["confirmAction"])}
    >
      <p id={textId}>{t("compactClock.overviewQuestion")}</p>
      <div className={styles["buttonGrid"]}>
        <button type="button" className={styles["button"]} onClick={props.onStay}>
          {t("compactClock.overviewStay")}
        </button>
        <button
          type="button"
          className={cx(styles["button"], styles["primary"])}
          onClick={props.onGo}
        >
          {t("compactClock.overviewGo")}
        </button>
      </div>
    </div>
  );

  return question;
}

/**
 * The compact clock with its panel, for a person who records own working time. The chip (in the
 * app bar on wide screens, the bar above the bottom navigation on phones) shows the state and
 * today's working time; it opens a panel with the warnings, the project for the clock-in, the next
 * step, the break and „Gehen“ – the same clock as the start page through {@link useClockControl}.
 *
 * @returns The chip and, while open, its panel.
 */
function CompactClockControl(): JSX.Element | null {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const whileShown = useWhileShown();
  const [open, setOpen] = useState(false);
  const [confirmClockOut, setConfirmClockOut] = useState(false);
  // The day overview waiting for the question about unsaved input of the page (F-78).
  const [overviewQuestion, setOverviewQuestion] = useState<LocalDate | null>(null);
  const askingRef = useRef(false);
  const hasUnsavedInput = useUnsavedInput();
  const chipRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const primaryActionRef = useRef<HTMLButtonElement>(null);
  // Whether the panel is open, for what follows a step after the panel may have been closed.
  const openRef = useRef(false);
  // „Abbrechen“ of „Gehen“ returns the focus to „Gehen“ once it is shown again.
  const refocusClockOutRef = useRef(false);
  const panelId = useId();
  const titleId = useId();
  const control = useClockControl({
    tickMs: compactTickMs,
    // A recorded step closes the panel if it is still open; the toast says what was recorded (F-61).
    // A question about the day overview keeps it open (F-78).
    onRecorded: () => {
      whileShown(() => {
        if (openRef.current && !askingRef.current) {
          closePanel(true);
        }
      });
    },
    // „Abfrage beim Ausstempeln“: the day overview opens on the start page (spec 6.5.4, P-2), but
    // never drops unsaved input of the current page unasked (F-78).
    onDayOverview: (date) => {
      whileShown(() => {
        const handover = dayOverviewHandover({
          panelOpen: openRef.current,
          unsavedInput: hasUnsavedInput(),
        });

        if (handover === "switch") {
          void navigate(routePaths.clock, { state: dayOverviewRequest(date) });
        } else if (handover === "ask") {
          askingRef.current = true;
          setOverviewQuestion(date);
        }
      });
    },
  });
  // Closing drops every step that still waits for a confirmation (early start, location
  // information, „Gehen“), so reopening the panel never skips one.
  const closePanel = (focusChip: boolean): void => {
    openRef.current = false;
    askingRef.current = false;
    setOpen(false);
    setConfirmClockOut(false);
    setOverviewQuestion(null);
    control?.cancelEarlyStart();
    control?.dismissLocationNotice();

    if (focusChip) {
      chipRef.current?.focus();
    }
  };
  // The listener outside React calls the close function of the latest render.
  const closePanelRef = useRef(closePanel);
  const [openedOn, setOpenedOn] = useState(pathname);

  // Another page closes the panel.
  if (openedOn !== pathname) {
    setOpenedOn(pathname);
    setOpen(false);
    setConfirmClockOut(false);
    setOverviewQuestion(null);
    control?.cancelEarlyStart();
    control?.dismissLocationNotice();
  }

  useEffect(() => {
    closePanelRef.current = closePanel;
  });

  useEffect(() => {
    openRef.current = open;

    if (!open) {
      return undefined;
    }

    panelRef.current?.focus();
    // A click or tap outside chip and panel closes the panel and leaves the focus where it went.
    const closeOutside = (event: PointerEvent): void => {
      const target = event.target;
      const inside =
        target instanceof Node &&
        (panelRef.current?.contains(target) === true || chipRef.current?.contains(target) === true);

      if (!inside) {
        closePanelRef.current(false);
      }
    };
    document.addEventListener("pointerdown", closeOutside);
    const unsubscribe = (): void => {
      document.removeEventListener("pointerdown", closeOutside);
    };

    return unsubscribe;
  }, [open]);

  if (control === null) {
    return null;
  }

  const { clock, day } = control;
  const seconds = compactClockSeconds(clock.state, day);
  const duration = seconds === null ? null : `${formatHoursMinutes(seconds)}\u00a0h`;
  const summary =
    duration === null
      ? control.statusText
      : t("compactClock.summary", { state: control.statusText, duration });
  // Warnings and a failed step show at the closed chip too: a sign and words, not colour alone.
  const attention = control.warnings.length > 0 || control.recordError !== null;
  const icon =
    clock.state === "Working" ? (
      <span className={compactStyles["dot"]} aria-hidden="true" />
    ) : clock.state === "OnBreak" ? (
      <Icon name="pause" className={cx(compactStyles["icon"], compactStyles["onBreak"])} />
    ) : (
      <Icon name="clock" className={compactStyles["icon"]} />
    );

  const onPanelKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    // A confirmation inside handles its own Escape and keeps the panel open.
    if (event.defaultPrevented) {
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      closePanel(true);
      return;
    }

    if (event.key !== "Tab" || panelRef.current === null) {
      return;
    }

    // The panel is placed at the end of the page; leaving it at either end goes back to the chip,
    // whose next stop is the account menu (on phones the navigation), as if the panel followed it.
    const focusables = [...panelRef.current.querySelectorAll<HTMLElement>(focusableSelector)];
    const first = focusables[0];
    const last = focusables.at(-1);
    const active = document.activeElement;
    const leavesBackward = event.shiftKey && (active === panelRef.current || active === first);
    const leavesForward = !event.shiftKey && (active === last || focusables.length === 0);

    if (leavesBackward || leavesForward) {
      event.preventDefault();
      closePanel(true);
    }
  };
  const onPanelBlur = (event: FocusEvent<HTMLDivElement>): void => {
    const next = event.relatedTarget;
    const stays =
      next instanceof Node &&
      (panelRef.current?.contains(next) === true || chipRef.current?.contains(next) === true);

    if (next !== null && !stays) {
      closePanel(false);
    }
  };

  const projectSwitcher = <ClockProjectSwitcher control={control} />;
  const panel = (
    <div
      ref={panelRef}
      id={panelId}
      role="dialog"
      aria-labelledby={titleId}
      tabIndex={-1}
      className={compactStyles["panel"]}
      onKeyDown={onPanelKeyDown}
      onBlur={onPanelBlur}
    >
      <div className={compactStyles["panelHeader"]}>
        <h2 id={titleId} className={compactStyles["title"]}>
          {t("compactClock.title")}
        </h2>
        <button
          type="button"
          className={cx(styles["button"], compactStyles["close"])}
          aria-label={t("compactClock.close")}
          onClick={() => {
            closePanel(true);
          }}
        >
          ×
        </button>
      </div>
      <p className={compactStyles["status"]}>
        <span className={clockStyles["stateDot"]} data-state={clock.state} aria-hidden="true" />
        {summary}
      </p>
      {overviewQuestion === null ? null : (
        <OverviewQuestion
          onGo={() => {
            askingRef.current = false;
            void navigate(routePaths.clock, { state: dayOverviewRequest(overviewQuestion) });
          }}
          onStay={() => {
            closePanel(true);
          }}
        />
      )}
      {day === null ? null : (
        <p className={styles["muted"]}>
          {t("compactClock.today", {
            work: formatHoursMinutes(day.workSeconds),
            break: formatHoursMinutes(day.breakSeconds),
          })}
        </p>
      )}

      <WarningList
        warnings={control.warnings}
        timeZone={control.timeZone}
        today={control.today}
        continuousLimitSeconds={control.limits.break.maxContinuousWorkSeconds}
      />

      {/* The project for the next clock-in is chosen before "Kommen" (UX-002). */}
      {clock.state === "Off" ? projectSwitcher : null}

      <div className={compactStyles["actions"]}>
        {control.primaryActions.map((action, index) => (
          <button
            key={action.kind}
            ref={index === 0 ? primaryActionRef : undefined}
            type="button"
            disabled={control.busy}
            className={cx(styles["button"], index === 0 ? styles["primary"] : undefined)}
            onClick={() => {
              control.onAction(action);
            }}
          >
            {control.actionLabel(action)}
          </button>
        ))}
        {control.standbyAction === undefined ? null : (
          <button
            type="button"
            disabled={control.busy}
            className={styles["button"]}
            onClick={() => {
              if (control.standbyAction !== undefined) {
                control.onAction(control.standbyAction);
              }
            }}
          >
            {control.actionLabel(control.standbyAction)}
          </button>
        )}
      </div>

      {control.confirmEarlyStart && control.earliestStart !== null ? (
        <EarlyStartConfirmation
          restEnd={control.timeText(control.earliestStart)}
          onConfirm={() => {
            control.onAction({ kind: "ClockIn", breakQualification: null });
          }}
          onCancel={() => {
            control.cancelEarlyStart();
            primaryActionRef.current?.focus();
          }}
        />
      ) : null}
      {control.noticeAction === null || control.location.policy === null ? null : (
        <LocationNotice
          policy={control.location.policy}
          onConfirm={control.confirmLocationNotice}
          onWithoutLocation={control.recordWithoutLocation}
          onDismiss={() => {
            control.dismissLocationNotice();
            primaryActionRef.current?.focus();
          }}
        />
      )}
      {control.location.locating ? (
        <p role="status" className={styles["muted"]}>
          {t("location.locating")}
        </p>
      ) : null}
      {control.recordError === null ? null : <Alert tone="danger">{control.recordError}</Alert>}
      {/* The lasting hint on the location capture, as on the start page (T-175). */}
      {control.location.policy === null ? null : (
        <p className={styles["muted"]}>
          {t("location.clockHint", { purpose: control.location.policy.purpose ?? "" })}
        </p>
      )}

      {control.clockOutAction === undefined ? null : confirmClockOut ? (
        <ClockOutConfirmation
          onConfirm={() => {
            setConfirmClockOut(false);

            if (control.clockOutAction !== undefined) {
              control.onAction(control.clockOutAction);
            }
          }}
          onCancel={() => {
            refocusClockOutRef.current = true;
            setConfirmClockOut(false);
          }}
        />
      ) : (
        <button
          ref={(element) => {
            if (element !== null && refocusClockOutRef.current) {
              refocusClockOutRef.current = false;
              element.focus();
            }
          }}
          type="button"
          disabled={control.busy}
          className={cx(styles["button"], compactStyles["clockOut"])}
          onClick={() => {
            setConfirmClockOut(true);
          }}
        >
          {control.actionLabel(control.clockOutAction)}
        </button>
      )}

      {/* Steps at a workplace NFC tag, as on the start page (T-174, F-78). */}
      <ClockNfc control={control} nested />

      {clock.state === "Off" ? null : projectSwitcher}

      <Link className={compactStyles["startLink"]} to={routePaths.clock}>
        {t("compactClock.openStart")}
      </Link>
    </div>
  );

  // The visible text starts the accessible name (WCAG 2.5.3): duration, then the state, then
  // „Stempeluhr“ and whether a warning waits.
  const compact = (
    <div className={compactStyles["root"]} data-compact-clock={clock.state}>
      <button
        ref={chipRef}
        type="button"
        className={compactStyles["chip"]}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        data-attention={attention ? "true" : undefined}
        onClick={() => {
          if (open) {
            closePanel(false);
          } else {
            setOpen(true);
          }
        }}
      >
        {icon}
        {attention ? <Icon name="warning" className={compactStyles["icon"]} /> : null}
        {duration === null ? null : (
          <span className={compactStyles["duration"]}>{duration}</span>
        )}{" "}
        <span
          className={duration === null ? compactStyles["stateWithout"] : compactStyles["state"]}
        >
          {duration === null ? control.statusText : `· ${control.statusText}`}
        </span>{" "}
        <span className="visually-hidden">
          {attention ? t("compactClock.suffixWarning") : t("compactClock.suffix")}
        </span>
        {/* Phones only: the panel opens upwards from the bar (F-77). */}
        <Icon name="chevronUp" className={compactStyles["expand"]} />
      </button>
      {open ? createPortal(panel, document.body) : null}
    </div>
  );

  return compact;
}

/**
 * The compact clock on every page (F-75, ADR-0200): on wide screens in the app bar between the
 * connectivity status and the account menu, on phones as a bar above the bottom navigation (F-77,
 * ADR-0201; `AppLayout` places it). Only for people who record their own working time and have an
 * employment, not on the start page „Heute“ with its full clock ({@link showsCompactClock}).
 *
 * @returns The clock, or `null` where it is not offered.
 */
export function CompactClock(): JSX.Element | null {
  const roles = useNavigationRoles();
  const hasEmployment = useHasEmployment();
  const { pathname } = useLocation();
  const shown = showsCompactClock({ roles, hasEmployment, pathname });
  const clock = shown ? <CompactClockControl /> : null;

  return clock;
}
